/**
 * Team Spain v0 — Servicio de Formularios, Carga y Salud Básica (M6)
 * 
 * Reglas de diseño y seguridad:
 * - Toda la persistencia es ONLINE en Supabase con RLS (nada en caché offline M5).
 * - El staff deportivo consulta cumplimiento y carga deportiva autorizada sin ver notas clínicas ni bienestar privado.
 * - Solo el médico autorizado publica disponibilidad operativa.
 * - Idempotencia en generación de pendientes y guardado de respuestas con control de revisiones.
 */

import { supabase } from '../../lib/supabase'
import {
  FormObligation,
  TrainingLogItem,
  WeeklyFormPayload,
  FormResponseItem,
  HealthIncidentItem,
  ClinicalNoteItem,
  AvailabilityAssessmentItem,
  ContactRequestItem,
  TeamComplianceItem,
  AthleteProfileData,
  WeeklyLoadAggregate,
  OperationalStatus,
  TeamFormSettings,
  CampDailyCheckinPayload,
  CampDailyComplianceItem,
  CampDailyMedicalInboxItem,
  CampDailyTrendItem,
} from '../../types/health'

export class FormsService {
  /**
   * Consulta las obligaciones pendientes de un atleta de forma idempotente.
   */
  async getAthletePendingObligations(teamId: string, athleteId: string): Promise<FormObligation[]> {
    if (!supabase) return []

    const { data, error } = await supabase.rpc('get_athlete_pending_obligations', {
      p_team_id: teamId,
      p_athlete_id: athleteId,
    })

    if (error) {
      console.error('Error al consultar obligaciones pendientes:', error)
      throw new Error(`Error al consultar obligaciones: ${error.message}`)
    }

    return (data as FormObligation[]) || []
  }

  /**
   * Consulta los datos de perfil deportivo de un atleta.
   */
  async getAthleteProfile(athleteId: string): Promise<AthleteProfileData | null> {
    if (!supabase) return null

    const { data, error } = await supabase
      .from('athletes')
      .select('id, first_name, last_name, dorsal_number, primary_position, secondary_positions, birth_date, modality, start_year, flag_club, tackle_club, city, country, is_profile_complete')
      .eq('id', athleteId)
      .maybeSingle()

    if (error) {
      console.error('Error al consultar perfil de atleta:', error)
      throw new Error(`Error al consultar perfil: ${error.message}`)
    }

    return data as AthleteProfileData | null
  }

  /**
   * Actualiza el perfil deportivo del propio atleta.
   */
  async updateAthleteProfile(athleteId: string, profile: Partial<AthleteProfileData>): Promise<void> {
    if (!supabase) return

    // Comprobar completitud requerida: first_name, last_name, birth_date, primary_position
    const isComplete =
      Boolean(profile.first_name?.trim()) &&
      Boolean(profile.last_name?.trim()) &&
      Boolean(profile.birth_date) &&
      Boolean(profile.primary_position?.trim())

    const updatePayload: Record<string, unknown> = {
      ...profile,
      is_profile_complete: isComplete,
      updated_at: new Date().toISOString(),
    }

    const { error } = await supabase
      .from('athletes')
      .update(updatePayload)
      .eq('id', athleteId)

    if (error) {
      console.error('Error al actualizar perfil de atleta:', error)
      throw new Error(`Error al actualizar perfil: ${error.message}`)
    }
  }

  /**
   * Consulta registros de entrenamiento (sesiones realizadas y sRPE).
   */
  async getTrainingLogs(
    teamId: string,
    athleteId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<TrainingLogItem[]> {
    if (!supabase) return []

    let query = supabase
      .from('training_logs')
      .select('*')
      .eq('team_id', teamId)
      .order('session_date', { ascending: false })
      .order('session_time', { ascending: false, nullsFirst: false })

    if (athleteId) {
      query = query.eq('athlete_id', athleteId)
    }
    if (startDate) {
      query = query.gte('session_date', startDate)
    }
    if (endDate) {
      query = query.lte('session_date', endDate)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error al consultar entrenamientos realizados:', error)
      throw new Error(`Error al consultar entrenamientos: ${error.message}`)
    }

    return (data as TrainingLogItem[]) || []
  }

  /**
   * Registra una sesión individual de entrenamiento realizada con carga sRPE.
   */
  async saveTrainingLog(log: Partial<TrainingLogItem>): Promise<TrainingLogItem> {
    if (!supabase) throw new Error('Supabase no está disponible')

    if (!log.duration_minutes || log.duration_minutes <= 0) {
      throw new Error('La duración debe ser mayor a 0 minutos.')
    }
    if (log.rpe === undefined || log.rpe < 0 || log.rpe > 10) {
      throw new Error('El RPE debe estar comprendido entre 0 y 10.')
    }

    const payload = {
      ...log,
      updated_at: new Date().toISOString(),
    }

    if (log.id) {
      const { data, error } = await supabase
        .from('training_logs')
        .update(payload)
        .eq('id', log.id)
        .select()
        .single()

      if (error) throw new Error(`Error al actualizar entrenamiento: ${error.message}`)
      return data as TrainingLogItem
    } else {
      const { data, error } = await supabase
        .from('training_logs')
        .insert(payload)
        .select()
        .single()

      if (error) throw new Error(`Error al guardar entrenamiento: ${error.message}`)
      return data as TrainingLogItem
    }
  }

  /**
   * Elimina un registro de entrenamiento.
   */
  async deleteTrainingLog(id: string): Promise<void> {
    if (!supabase) return

    const { error } = await supabase.from('training_logs').delete().eq('id', id)
    if (error) throw new Error(`Error al eliminar entrenamiento: ${error.message}`)
  }

  /**
   * Consulta la respuesta de un formulario semanal para un periodo concreto.
   */
  async getWeeklyFormResponse(
    teamId: string,
    athleteId: string,
    periodStart: string
  ): Promise<FormResponseItem | null> {
    if (!supabase) return null

    const { data, error } = await supabase
      .from('form_responses')
      .select('*')
      .eq('team_id', teamId)
      .eq('athlete_id', athleteId)
      .eq('form_type', 'weekly')
      .eq('period_start', periodStart)
      .maybeSingle()

    if (error) {
      console.error('Error al consultar respuesta semanal:', error)
      throw new Error(`Error al consultar respuesta semanal: ${error.message}`)
    }

    return data as FormResponseItem | null
  }

  /**
   * Consulta las respuestas a formularios semanales de un atleta (o todas si es medic).
   */
  async getFormResponses(teamId: string, athleteId?: string): Promise<FormResponseItem[]> {
    if (!supabase) return []

    let query = supabase
      .from('form_responses')
      .select('*')
      .eq('team_id', teamId)
      .order('period_start', { ascending: false })

    if (athleteId) {
      query = query.eq('athlete_id', athleteId)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error al consultar respuestas de formularios:', error)
      throw new Error(`Error al consultar respuestas: ${error.message}`)
    }

    return (data as FormResponseItem[]) || []
  }

  /**
   * Guarda un borrador online o envía definitivamente un formulario semanal.
   */
  async saveWeeklyFormResponse(
    teamId: string,
    athleteId: string,
    periodStart: string,
    periodEnd: string,
    payload: WeeklyFormPayload,
    isDraft: boolean
  ): Promise<{ success: boolean; response_id: string; is_late: boolean; status: string }> {
    if (!supabase) throw new Error('Supabase no está disponible')

    const cleanPayload = {
      ...payload,
      draft_saved_at: isDraft ? new Date().toISOString() : undefined,
    }

    const { data, error } = await supabase.rpc('save_form_response_atomic', {
      p_team_id: teamId,
      p_athlete_id: athleteId,
      p_form_type: 'weekly',
      p_period_start: periodStart,
      p_period_end: periodEnd,
      p_payload: cleanPayload,
      p_is_draft: isDraft,
    })

    if (error) {
      console.error('Error al guardar respuesta de formulario semanal:', error)
      throw new Error(`Error al guardar formulario: ${error.message}`)
    }

    return data as { success: boolean; response_id: string; is_late: boolean; status: string }
  }

  /**
   * Consulta el cumplimiento del equipo para el cuerpo técnico (sin fuga de datos clínicos).
   */
  async getTeamCompliance(teamId: string, periodStart: string): Promise<TeamComplianceItem[]> {
    if (!supabase) return []

    const { data, error } = await supabase.rpc('get_team_form_compliance', {
      p_team_id: teamId,
      p_period_start: periodStart,
    })

    if (error) {
      console.error('Error al consultar cumplimiento del equipo:', error)
      throw new Error(`Error al consultar cumplimiento: ${error.message}`)
    }

    return (data as TeamComplianceItem[]) || []
  }

  /**
   * Consulta incidencias de salud registradas (privadas para atleta y médico).
   */
  async getHealthIncidents(teamId: string, athleteId?: string): Promise<HealthIncidentItem[]> {
    if (!supabase) return []

    let query = supabase
      .from('health_incidents_private')
      .select(`
        *,
        athlete:athletes!health_incidents_private_athlete_id_fkey (
          id, first_name, last_name, dorsal_number
        )
      `)
      .eq('team_id', teamId)
      .order('incident_date', { ascending: false })
      .order('created_at', { ascending: false })

    if (athleteId) {
      query = query.eq('athlete_id', athleteId)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error al consultar incidencias de salud:', error)
      throw new Error(`Error al consultar incidencias: ${error.message}`)
    }

    return (data || []).map((row: any) => ({
      ...row,
      athlete_name: row.athlete
        ? `${row.athlete.first_name} ${row.athlete.last_name}`.trim()
        : undefined,
    })) as HealthIncidentItem[]
  }

  /**
   * Crea una nueva incidencia de salud o una actualización ligada a un episodio existente.
   */
  async createHealthIncident(incident: Partial<HealthIncidentItem>): Promise<HealthIncidentItem> {
    if (!supabase) throw new Error('Supabase no está disponible')

    const payload = {
      ...incident,
      incident_date: incident.incident_date || new Date().toISOString().split('T')[0],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    const { data, error } = await supabase
      .from('health_incidents_private')
      .insert(payload)
      .select()
      .single()

    if (error) {
      console.error('Error al registrar incidencia de salud:', error)
      throw new Error(`Error al registrar incidencia: ${error.message}`)
    }

    // Si solicitó contacto explícitamente y no viene de formulario
    if (incident.contact_requested && incident.athlete_id && incident.team_id) {
      await supabase.from('contact_requests').insert({
        team_id: incident.team_id,
        athlete_id: incident.athlete_id,
        incident_id: data.id,
        status: 'pending',
        priority: 'normal',
        reason: `Incidencia en ${incident.body_zone}: ${incident.contact_comment || incident.description || 'Solicita valoración con sanitario.'}`,
      })
    }

    return data as HealthIncidentItem
  }

  /**
   * Actualiza una incidencia (ej. pautas compartidas o estado del episodio).
   */
  async updateHealthIncident(id: string, updates: Partial<HealthIncidentItem>): Promise<void> {
    if (!supabase) return

    const { error } = await supabase
      .from('health_incidents_private')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)

    if (error) {
      console.error('Error al actualizar incidencia de salud:', error)
      throw new Error(`Error al actualizar incidencia: ${error.message}`)
    }
  }

  /**
   * Cierra un episodio de lesión/molestia (acción del médico).
   */
  async closeIncidentEpisode(episodeId: string, sharedNotes?: string): Promise<void> {
    if (!supabase) return

    const now = new Date().toISOString()
    const { error } = await supabase
      .from('health_incidents_private')
      .update({
        status: 'resolved',
        is_active: false,
        closed_at: now,
        shared_notes: sharedNotes,
        updated_at: now,
      })
      .or(`id.eq.${episodeId},episode_id.eq.${episodeId}`)

    if (error) {
      console.error('Error al cerrar episodio de salud:', error)
      throw new Error(`Error al cerrar episodio: ${error.message}`)
    }
  }

  /**
   * Consulta las notas clínicas internas de un incidente (estrictamente exclusivo de medic).
   */
  async getClinicalNotes(incidentId: string): Promise<ClinicalNoteItem | null> {
    if (!supabase) return null

    const { data, error } = await supabase
      .from('health_incident_clinical_notes')
      .select('*')
      .eq('incident_id', incidentId)
      .maybeSingle()

    if (error) {
      // Si el rol no es medic, RLS devolverá 0 filas o error
      return null
    }

    return data as ClinicalNoteItem | null
  }

  /**
   * Guarda o actualiza notas clínicas internas y diagnóstico (exclusivo de medic).
   */
  async saveClinicalNotes(
    incidentId: string,
    teamId: string,
    internalNotes: string,
    diagnosis?: string
  ): Promise<void> {
    if (!supabase) return

    const { error } = await supabase
      .from('health_incident_clinical_notes')
      .upsert({
        incident_id: incidentId,
        team_id: teamId,
        internal_notes: internalNotes,
        diagnosis: diagnosis || null,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'incident_id' })

    if (error) {
      console.error('Error al guardar notas clínicas internas:', error)
      throw new Error(`Error al guardar notas clínicas: ${error.message}`)
    }
  }

  /**
   * Consulta solicitudes de contacto entre jugador y médico.
   */
  async getContactRequests(teamId: string): Promise<ContactRequestItem[]> {
    if (!supabase) return []

    const { data, error } = await supabase
      .from('contact_requests')
      .select(`
        *,
        athlete:athletes!contact_requests_athlete_id_fkey (
          id, first_name, last_name, dorsal_number
        ),
        assigned_profile:profiles!contact_requests_assigned_to_fkey (
          full_name
        )
      `)
      .eq('team_id', teamId)
      .order('requested_at', { ascending: false })

    if (error) {
      console.error('Error al consultar solicitudes de contacto:', error)
      throw new Error(`Error al consultar solicitudes: ${error.message}`)
    }

    return (data || []).map((row: any) => ({
      ...row,
      athlete_name: row.athlete
        ? `${row.athlete.first_name} ${row.athlete.last_name}`.trim()
        : undefined,
      dorsal_number: row.athlete?.dorsal_number,
      assigned_to_name: row.assigned_profile?.full_name,
    })) as ContactRequestItem[]
  }

  /**
   * Actualiza el estado de una solicitud de contacto (en curso o resuelta).
   */
  async updateContactRequest(
    id: string,
    status: 'in_progress' | 'resolved',
    resolutionNotes?: string
  ): Promise<void> {
    if (!supabase) return

    const now = new Date().toISOString()
    const { error } = await supabase
      .from('contact_requests')
      .update({
        status,
        resolution_notes: resolutionNotes,
        resolved_at: status === 'resolved' ? now : null,
        updated_at: now,
      })
      .eq('id', id)

    if (error) {
      console.error('Error al actualizar solicitud de contacto:', error)
      throw new Error(`Error al actualizar solicitud: ${error.message}`)
    }
  }

  /**
   * Consulta las valoraciones de disponibilidad operativa publicadas (públicas para staff y jugador).
   */
  async getAvailabilityAssessments(
    teamId: string,
    athleteId?: string
  ): Promise<AvailabilityAssessmentItem[]> {
    if (!supabase) return []

    let query = supabase
      .from('availability_assessments')
      .select(`
        *,
        athlete:athletes!availability_assessments_athlete_id_fkey (
          id, first_name, last_name, dorsal_number
        ),
        assessed_profile:profiles!availability_assessments_assessed_by_fkey (
          full_name
        )
      `)
      .eq('team_id', teamId)
      .order('valid_from', { ascending: false })
      .order('created_at', { ascending: false })

    if (athleteId) {
      query = query.eq('athlete_id', athleteId)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error al consultar valoraciones de disponibilidad:', error)
      throw new Error(`Error al consultar valoraciones: ${error.message}`)
    }

    return (data || []).map((row: any) => ({
      ...row,
      athlete_name: row.athlete
        ? `${row.athlete.first_name} ${row.athlete.last_name}`.trim()
        : undefined,
      dorsal_number: row.athlete?.dorsal_number,
      assessed_by_name: row.assessed_profile?.full_name,
    })) as AvailabilityAssessmentItem[]
  }

  /**
   * Publica la disponibilidad operativa y restricciones compartidas (exclusivo de medic).
   */
  async publishAvailability(
    teamId: string,
    athleteId: string,
    status: OperationalStatus,
    operationalRestriction?: string,
    sharedInstructions?: string,
    validFrom?: string,
    nextReviewDate?: string
  ): Promise<void> {
    if (!supabase) throw new Error('Supabase no está disponible')

    const { error } = await supabase.rpc('publish_availability_assessment', {
      p_team_id: teamId,
      p_athlete_id: athleteId,
      p_status: status,
      p_operational_restriction: operationalRestriction || null,
      p_shared_instructions: sharedInstructions || null,
      p_valid_from: validFrom || new Date().toISOString().split('T')[0],
      p_next_review_date: nextReviewDate || null,
    })

    if (error) {
      console.error('Error al publicar disponibilidad operativa:', error)
      throw new Error(`Error al publicar disponibilidad: ${error.message}`)
    }
  }

  /**
   * Calcula agregados semanales de carga sRPE sin doble contabilización.
   */
  calculateWeeklyAggregates(logs: TrainingLogItem[]): WeeklyLoadAggregate[] {
    const weekMap = new Map<string, {
      week_start: string
      week_end: string
      total_minutes: number
      total_load: number
      session_count: number
      unique_days: Set<string>
      flag_minutes: number
      strength_minutes: number
      other_minutes: number
      flag_load: number
      strength_load: number
      rpe_sum: number
    }>()

    for (const log of logs) {
      // Calcular lunes de la fecha del log
      const logDate = new Date(log.session_date + 'T12:00:00Z')
      const day = logDate.getUTCDay()
      const diffToMonday = (day === 0 ? -6 : 1) - day
      const monday = new Date(logDate)
      monday.setUTCDate(logDate.getUTCDate() + diffToMonday)
      const sunday = new Date(monday)
      sunday.setUTCDate(monday.getUTCDate() + 6)

      const mondayStr = monday.toISOString().split('T')[0]
      const sundayStr = sunday.toISOString().split('T')[0]

      if (!weekMap.has(mondayStr)) {
        weekMap.set(mondayStr, {
          week_start: mondayStr,
          week_end: sundayStr,
          total_minutes: 0,
          total_load: 0,
          session_count: 0,
          unique_days: new Set<string>(),
          flag_minutes: 0,
          strength_minutes: 0,
          other_minutes: 0,
          flag_load: 0,
          strength_load: 0,
          rpe_sum: 0,
        })
      }

      const w = weekMap.get(mondayStr)!
      const load = Number(log.session_load || log.duration_minutes * log.rpe)
      w.total_minutes += log.duration_minutes
      w.total_load += load
      w.session_count += 1
      w.rpe_sum += log.rpe
      w.unique_days.add(log.session_date)

      if (log.activity_type === 'flag') {
        w.flag_minutes += log.duration_minutes
        w.flag_load += load
      } else if (log.activity_type === 'strength') {
        w.strength_minutes += log.duration_minutes
        w.strength_load += load
      } else {
        w.other_minutes += log.duration_minutes
      }
    }

    return Array.from(weekMap.values())
      .map((w) => ({
        week_start: w.week_start,
        week_end: w.week_end,
        total_minutes: w.total_minutes,
        total_load: Math.round(w.total_load * 10) / 10,
        session_count: w.session_count,
        unique_days: w.unique_days.size,
        flag_minutes: w.flag_minutes,
        strength_minutes: w.strength_minutes,
        other_minutes: w.other_minutes,
        flag_load: Math.round(w.flag_load * 10) / 10,
        strength_load: Math.round(w.strength_load * 10) / 10,
        average_rpe: w.session_count > 0 ? Math.round((w.rpe_sum / w.session_count) * 10) / 10 : 0,
      }))
      .sort((a, b) => b.week_start.localeCompare(a.week_start))
  }

  /**
   * Consulta la configuración horaria de apertura y entrega semanal del equipo.
   */
  async getTeamFormSettings(teamId: string): Promise<TeamFormSettings | null> {
    if (!supabase) return null

    const { data, error } = await supabase
      .from('team_form_settings')
      .select('*')
      .eq('team_id', teamId)
      .maybeSingle()

    if (error) {
      console.error('Error al consultar configuración horaria:', error)
      throw new Error(`Error al consultar configuración horaria: ${error.message}`)
    }

    return (data as TeamFormSettings) || null
  }

  /**
   * Actualiza la configuración de apertura y entrega semanal (solo staff autorizado: coach, admin, medic).
   */
  async updateTeamFormSettings(
    teamId: string,
    settings: Partial<Omit<TeamFormSettings, 'team_id' | 'created_at' | 'updated_at'>>
  ): Promise<void> {
    if (!supabase) return

    const { error } = await supabase
      .from('team_form_settings')
      .upsert({
        team_id: teamId,
        ...settings,
        updated_at: new Date().toISOString(),
      })

    if (error) {
      console.error('Error al actualizar configuración horaria:', error)
      throw new Error(`Error al actualizar configuración horaria: ${error.message}`)
    }
  }

  /**
   * Guarda un borrador online o envía definitivamente un check-in diario de concentración.
   * Soporta llamada con objeto de parámetros o parámetros posicionales.
   */
  async saveCampDailyCheckin(
    paramsOrTeamId:
      | string
      | {
          teamId: string
          athleteId: string
          eventId: string
          checkinDate?: string
          targetDate?: string
          payload: CampDailyCheckinPayload
          isDraft?: boolean
          status?: 'draft' | 'submitted'
        },
    athleteIdArg?: string,
    eventIdArg?: string,
    checkinDateArg?: string,
    payloadArg?: CampDailyCheckinPayload,
    isDraftArg?: boolean
  ): Promise<{ success: boolean; response_id: string; is_late: boolean; status: string; revision: number; due_at: string }> {
    if (!supabase) throw new Error('Supabase no está disponible')

    let teamId: string
    let athleteId: string
    let eventId: string
    let checkinDate: string
    let payload: CampDailyCheckinPayload
    let isDraft: boolean

    if (typeof paramsOrTeamId === 'object') {
      teamId = paramsOrTeamId.teamId
      athleteId = paramsOrTeamId.athleteId
      eventId = paramsOrTeamId.eventId
      checkinDate = paramsOrTeamId.targetDate || paramsOrTeamId.checkinDate || new Date().toISOString().slice(0, 10)
      payload = paramsOrTeamId.payload
      isDraft = paramsOrTeamId.isDraft ?? (paramsOrTeamId.status === 'draft')
    } else {
      teamId = paramsOrTeamId
      athleteId = athleteIdArg!
      eventId = eventIdArg!
      checkinDate = checkinDateArg!
      payload = payloadArg!
      isDraft = isDraftArg ?? false
    }

    const cleanPayload = {
      ...payload,
      draft_saved_at: isDraft ? new Date().toISOString() : undefined,
    }

    const { data, error } = await supabase.rpc('save_camp_daily_checkin_atomic', {
      p_team_id: teamId,
      p_athlete_id: athleteId,
      p_event_id: eventId,
      p_checkin_date: checkinDate,
      p_payload: cleanPayload,
      p_is_draft: isDraft,
    })

    if (error) {
      console.error('Error al guardar check-in diario de concentración:', error)
      throw new Error(`Error al guardar check-in: ${error.message}`)
    }

    return data as { success: boolean; response_id: string; is_late: boolean; status: string; revision: number; due_at: string }
  }

  /**
   * Consulta el check-in diario de un atleta para una fecha determinada.
   * Soporta llamada con objeto de parámetros o parámetros posicionales.
   */
  async getCampDailyCheckin(
    athleteIdOrParams:
      | string
      | {
          athleteId: string
          targetDate?: string
          checkinDate?: string
          teamId?: string
          eventId?: string
        },
    checkinDateArg?: string
  ): Promise<FormResponseItem | null> {
    if (!supabase) return null

    let athleteId: string
    let checkinDate: string

    if (typeof athleteIdOrParams === 'object') {
      athleteId = athleteIdOrParams.athleteId
      checkinDate = athleteIdOrParams.targetDate || athleteIdOrParams.checkinDate || ''
    } else {
      athleteId = athleteIdOrParams
      checkinDate = checkinDateArg || ''
    }

    const { data, error } = await supabase
      .from('form_responses')
      .select('*')
      .eq('athlete_id', athleteId)
      .eq('form_type', 'camp_daily')
      .eq('period_start', checkinDate)
      .maybeSingle()

    if (error) {
      console.error('Error al consultar check-in diario:', error)
      throw new Error(`Error al consultar check-in diario: ${error.message}`)
    }

    return data as FormResponseItem | null
  }

  /**
   * Consulta incidencias abiertas o en seguimiento de un atleta (alias para CampsView).
   */
  async getAthleteIncidents(athleteId: string, teamId: string = '33333333-3333-3333-3333-333333333333'): Promise<HealthIncidentItem[]> {
    return this.getHealthIncidents(teamId, athleteId)
  }

  /**
   * Consulta el informe de cumplimiento de check-in diario (para coach/staff deportivo, CERO datos clínicos).
   */
  async getCampDailyCompliance(
    teamId: string,
    eventId: string,
    checkinDate: string
  ): Promise<CampDailyComplianceItem[]> {
    if (!supabase) return []

    const { data, error } = await supabase.rpc('get_camp_daily_compliance', {
      p_team_id: teamId,
      p_event_id: eventId,
      p_checkin_date: checkinDate,
    })

    if (error) {
      console.error('Error al consultar cumplimiento diario:', error)
      throw new Error(`Error al consultar cumplimiento diario: ${error.message}`)
    }

    return (data as CampDailyComplianceItem[]) || []
  }

  /**
   * Consulta la bandeja médica de check-in diario (exclusivo para rol medic).
   */
  async getCampDailyMedicalInbox(
    teamId: string,
    eventId: string,
    checkinDate: string
  ): Promise<CampDailyMedicalInboxItem[]> {
    if (!supabase) return []

    const { data, error } = await supabase.rpc('get_camp_daily_medical_inbox', {
      p_team_id: teamId,
      p_event_id: eventId,
      p_checkin_date: checkinDate,
    })

    if (error) {
      console.error('Error al consultar bandeja médica de check-in:', error)
      throw new Error(`Error al consultar bandeja médica: ${error.message}`)
    }

    return (data as CampDailyMedicalInboxItem[]) || []
  }

  /**
   * Consulta las tendencias y agregados de check-in diario durante una concentración.
   */
  async getCampDailyTrends(
    teamId: string,
    eventId: string
  ): Promise<CampDailyTrendItem[]> {
    if (!supabase) return []

    const { data, error } = await supabase.rpc('get_camp_daily_trends', {
      p_team_id: teamId,
      p_event_id: eventId,
    })

    if (error) {
      console.error('Error al consultar evolución diaria de concentración:', error)
      throw new Error(`Error al consultar evolución: ${error.message}`)
    }

    return (data as CampDailyTrendItem[]) || []
  }

  /**
   * Actualiza la hora límite diaria de check-in de una concentración o del equipo.
   */
  async updateDailyDueTime(
    teamId: string,
    eventId: string | null,
    dueTime: string
  ): Promise<void> {
    if (!supabase) return

    if (eventId) {
      const { error } = await supabase
        .from('events')
        .update({ daily_checkin_due_time: dueTime, updated_at: new Date().toISOString() })
        .eq('id', eventId)

      if (error) {
        console.error('Error al actualizar hora límite de la concentración:', error)
        throw new Error(`Error al actualizar hora límite: ${error.message}`)
      }
    } else {
      const { error } = await supabase
        .from('team_form_settings')
        .update({ daily_checkin_due_time: dueTime, updated_at: new Date().toISOString() })
        .eq('team_id', teamId)

      if (error) {
        console.error('Error al actualizar hora límite del equipo:', error)
        throw new Error(`Error al actualizar hora límite: ${error.message}`)
      }
    }
  }
}

export const formsService = new FormsService()

